import React from "react";
import { FacebookIcon, InstagramIcon, LinkedinIcon, PaperclipIcon, YoutubeIcon, BoxIcon } from "lucide-react";
import { EmailIdentity, SocialNetwork } from "../../types/emailIdentity";
import { CityWordmark } from "./CityWordmark";
interface EmailTemplateProps {
  identity: EmailIdentity;
  cityName: string;
  title: string;
  /** Resolved rich HTML of the body; when null, `placeholder` is shown instead. */
  bodyHtml: string | null;
  placeholder: string;
  attachments?: {
    id: string;
    name: string;
  }[];
  lang: 'fr' | 'en';
  /** Larger typography and spacing (expanded preview). */
  large?: boolean;
}
const SOCIAL_ICONS: Record<SocialNetwork, {
  icon: BoxIcon;
  label: string;
}> = {
  facebook: {
    icon: FacebookIcon,
    label: 'Facebook'
  },
  instagram: {
    icon: InstagramIcon,
    label: 'Instagram'
  },
  linkedin: {
    icon: LinkedinIcon,
    label: 'LinkedIn'
  },
  youtube: {
    icon: YoutubeIcon,
    label: 'YouTube'
  }
};

/** Branded email: compact header, notice content and a short centred footer. */
export function EmailTemplate({
  identity,
  cityName,
  title,
  bodyHtml,
  placeholder,
  attachments = [],
  lang,
  large = false
}: EmailTemplateProps) {
  const tr = (fr: string, en: string) => lang === 'fr' ? fr : en;
  const brand = identity.headerStyle === 'brand';
  const dark = identity.footerStyle === 'dark';
  const cityLabel = tr(`Ville de ${cityName}`, `City of ${cityName}`);
  const footerLines = identity.footerText.split('\n').filter(l => l.trim());
  const slogan = identity.slogan[lang];
  const pad = large ? 'px-10' : 'px-5';
  const small = large ? 'text-xs' : 'text-[10px]';
  const footerText = dark ? 'rgba(255,255,255,0.65)' : '#718096';
  const footerStrong = dark ? '#FFFFFF' : '#2D3748';
  return <div className={`bg-slate-100 ${large ? 'py-8 px-6' : 'py-4 px-3'}`} data-id="element-4139" data-mp="tj6nabz03pdq">
      <div className="mx-auto max-w-[600px] bg-white rounded-lg overflow-hidden shadow-sm border border-slate-200" data-id="element-4140" data-mp="tlazriaytwpm">
        {/* Header */}
        <header className={`${pad} ${large ? 'py-5' : 'py-3.5'} flex items-center justify-between gap-3`} style={brand ? {
        backgroundColor: identity.primaryColor
      } : undefined} data-id="element-4141" data-mp="2izsf4bz21jw2">
          <div className="min-w-0" data-id="element-4142" data-mp="29b23s9i4kjke">
            {identity.logoUrl ? <img src={identity.logoUrl} alt={cityLabel} className={`${large ? 'h-12' : 'h-9'} w-auto max-w-[240px] object-contain object-left ${brand ? 'brightness-0 invert' : ''}`} data-id="element-4143" data-mp="29a8w96bcdq17" /> : <CityWordmark cityName={cityName} color={identity.primaryColor} inverted={brand} large={large} data-id="element-4144" data-mp="209183h2ji5ku" />}
            {slogan && <p className={`${large ? 'text-xs mt-1.5' : 'text-[10px] mt-1'} leading-none truncate`} style={{
            color: brand ? 'rgba(255,255,255,0.85)' : '#4A5568'
          }} data-id="element-4145" data-mp="25a8htlo8teu5">
                {slogan}
              </p>}
          </div>
          {identity.headerTagline[lang] && <span className={`${large ? 'text-[11px]' : 'text-[9px]'} shrink-0 font-medium uppercase tracking-wider`} style={{
          color: brand ? 'rgba(255,255,255,0.8)' : '#A0AEC0'
        }} data-id="element-4146" data-mp="3kqz2uqjogjvc">
              {identity.headerTagline[lang]}
            </span>}
        </header>
        <div className="h-[3px]" style={{
        backgroundColor: identity.accentColor
      }} aria-hidden="true" data-id="element-4147" data-mp="1933qk9h6i1fw" />

        {/* Content */}
        <div className={`${pad} ${large ? 'py-8' : 'py-5'}`} data-id="element-4148" data-mp="1nsjhbpgpm6vl">
          <h1 className={`${large ? 'text-2xl mb-4' : 'text-lg mb-3'} font-bold text-slate-900 leading-snug`} data-id="element-4149" data-mp="1izgtlxym5rnb">{title}</h1>
          {bodyHtml ? <div className={`${large ? 'text-[15px]' : 'text-sm'} text-slate-600 leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_b]:font-semibold [&_strong]:font-semibold [&_img]:w-full [&_img]:rounded-lg [&_img]:my-3 [&_a]:underline [&_a]:font-medium`} dangerouslySetInnerHTML={{
          __html: bodyHtml.replace(/<a /g, `<a style="color:${identity.primaryColor}" `)
        }} data-id="element-4150" data-mp="1wq3jr1qb697s" /> : <p className={`${large ? 'text-[15px]' : 'text-sm'} text-slate-400 leading-relaxed`} data-id="element-4151" data-mp="ccupoksigjog">{placeholder}</p>}

          {attachments.length > 0 && <div className="mt-5 pt-4 border-t border-slate-100" data-id="element-4152" data-mp="7fbexuv0up19">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2" data-id="element-4153" data-mp="8tm5n78n6gl8">
                {attachments.length} {tr('pièce(s) jointe(s)', 'attachment(s)')}
              </p>
              <ul className="space-y-1.5" data-id="element-4154" data-mp="isuxnujy5l4y">
                {attachments.map(file => <li key={file.id} className="flex items-center gap-2 border border-slate-200 rounded-md px-2.5 py-1.5" data-id="element-4155" data-mp="nrob5mz8hc54">
                    <PaperclipIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" aria-hidden="true" data-id="element-4156" data-mp="3mad1lqwlm803" />
                    <span className="text-xs text-slate-600 truncate" data-id="element-4157" data-mp="3r8mxk3q5dcmr">{file.name}</span>
                  </li>)}
              </ul>
            </div>}
        </div>

        {/* Footer — compact and centred, like a transactional email */}
        <footer className={`${pad} ${large ? 'py-6' : 'py-4'} text-center`} style={{
        backgroundColor: dark ? '#1E1E1E' : '#FFFFFF',
        borderTop: dark ? undefined : '1px solid #EDF2F7'
      }} data-id="element-4158" data-mp="4udabxxyeank">
          {identity.showSocial && identity.socials.length > 0 && <div className="flex justify-center gap-3 mb-3" aria-label={tr('Réseaux sociaux', 'Social media')} data-id="element-4159" data-mp="1plzfybwnsgx2">
              {identity.socials.map(network => {
            const {
              icon: Icon,
              label
            } = SOCIAL_ICONS[network];
            return <Icon key={network} className={large ? 'w-4 h-4' : 'w-3.5 h-3.5'} style={{
              color: dark ? 'rgba(255,255,255,0.7)' : '#A0AEC0'
            }} aria-label={label} data-id="element-4160" data-mp="1bnj98kysqqex" />;
          })}
            </div>}

          <div className={`${small} leading-relaxed`} style={{
          color: footerText
        }} data-id="element-4161" data-mp="11w5705fgnci3">
            {footerLines[0] && <p className="font-semibold" style={{
            color: footerStrong
          }} data-id="element-4162" data-mp="xt8t2s0v84kh">
                {footerLines[0]}
              </p>}
            {footerLines.length > 1 && <p data-id="element-4163" data-mp="nvueyz2ezrvc">{footerLines.slice(1).join(' · ')}</p>}
          </div>

          {(identity.email || identity.website) && <p className={`${small} mt-1`} data-id="element-4164" data-mp="iyuhvdnjlzgd">
              {[identity.email, identity.website].filter(Boolean).map((item, i) => <React.Fragment key={item} data-id="element-4165" data-mp="9cjjsb0dzxz1" data-mpi={i}>
                  {i > 0 && <span style={{
              color: footerText
            }} data-id="element-4166" data-mp="eakzqn2inh6a"> · </span>}
                  <span className="underline underline-offset-2" style={{
              color: dark ? '#FFFFFF' : identity.primaryColor
            }} data-id="element-4167" data-mp="ak01axzmfse">
                    {item}
                  </span>
                </React.Fragment>)}
            </p>}

          {identity.showUnsubscribe && <p className={`${large ? 'text-[11px]' : 'text-[9px]'} mt-3 leading-relaxed`} style={{
          color: dark ? 'rgba(255,255,255,0.45)' : '#A0AEC0'
        }} data-id="element-4168" data-mp="wzrl6hup2mvs">
              {tr(`Vous recevez ce courriel parce que vous êtes inscrit aux avis de la ${cityLabel}.`, `You are receiving this email because you subscribed to ${cityLabel} notices.`)}
              <br data-id="element-4169" data-mp="1ayiwxxm33wgr" />
              <span className="underline" data-id="element-4170" data-mp="1axzf0gwfp82q">{tr('Gérer mes préférences', 'Manage preferences')}</span>
              {' · '}
              <span className="underline" data-id="element-4171" data-mp="30go7rvz6ig4k">{tr('Se désabonner', 'Unsubscribe')}</span>
            </p>}
        </footer>
      </div>
    </div>;
}